import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, CheckCircle, Eye, EyeOff, Trash2, RefreshCw } from 'lucide-react';
import { useSecurity } from '../contexts/SecurityContext.js';

/**
 * Security Dashboard Component
 * Shows security events and monitoring (development only)
 */
const SecurityDashboard = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('events');
  const { securityEvents, getSecuritySummary, clearEvents, logEvent } = useSecurity();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    setSummary(getSecuritySummary());
  }, [securityEvents, getSecuritySummary]);

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const tabs = [
    { id: 'events', label: 'Events', icon: Shield },
    { id: 'summary', label: 'Summary', icon: CheckCircle },
    { id: 'settings', label: 'Settings', icon: AlertTriangle }
  ];

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getEventIcon = (event) => {
    switch (event) {
      case 'XSS_ATTEMPT_DETECTED':
      case 'SUSPICIOUS_FORM_SUBMISSION':
      case 'SUSPICIOUS_API_CALL':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'RATE_LIMIT_EXCEEDED':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'SUCCESSFUL_LOGIN':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      default:
        return <Shield className="w-4 h-4 text-blue-500" />;
    }
  };

  const getEventColor = (event) => {
    switch (event) {
      case 'XSS_ATTEMPT_DETECTED':
      case 'SUSPICIOUS_FORM_SUBMISSION':
      case 'SUSPICIOUS_API_CALL':
        return 'bg-red-50 border-red-200';
      case 'RATE_LIMIT_EXCEEDED':
        return 'bg-yellow-50 border-yellow-200';
      case 'SUCCESSFUL_LOGIN':
        return 'bg-green-50 border-green-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const testSecurityEvent = () => {
    logEvent('TEST_EVENT', {
      message: 'This is a test security event',
      timestamp: new Date().toISOString()
    });
  };

  return (
    <div className="fixed bottom-4 left-4 z-50">
      {/* Toggle Button */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        className="bg-red-600 text-white p-3 rounded-full shadow-lg hover:bg-red-700 transition-colors"
        title="Security Dashboard"
      >
        <Shield className="w-5 h-5" />
      </button>

      {/* Dashboard Panel */}
      {isVisible && (
        <div className="absolute bottom-16 left-0 w-96 bg-white rounded-lg shadow-xl border max-h-96 overflow-hidden">
          {/* Header */}
          <div className="bg-red-50 px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Security Dashboard</h3>
              <button
                onClick={() => setIsVisible(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium ${
                    activeTab === tab.id
                      ? 'bg-red-50 text-red-700 border-b-2 border-red-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="p-4 max-h-64 overflow-y-auto">
            {activeTab === 'events' && (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-gray-900">Security Events</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={testSecurityEvent}
                      className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                    >
                      Test
                    </button>
                    <button
                      onClick={clearEvents}
                      className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {securityEvents.length === 0 ? (
                  <div className="text-center text-gray-500 py-4">
                    <Shield className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>No security events</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {securityEvents.slice(-10).reverse().map((event) => (
                      <div
                        key={event.id}
                        className={`p-3 rounded border ${getEventColor(event.event)}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center">
                            {getEventIcon(event.event)}
                            <span className="ml-2 text-sm font-medium">
                              {event.event.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatTimestamp(event.timestamp)}
                          </span>
                        </div>
                        {event.details && Object.keys(event.details).length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs text-gray-600 cursor-pointer">
                              Details
                            </summary>
                            <pre className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">
                              {JSON.stringify(event.details, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'summary' && summary && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">24-Hour Summary</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-sm font-medium text-gray-600">Total Events</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {summary.totalEvents}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-3 rounded">
                    <div className="text-sm font-medium text-gray-600">Event Types</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {Object.keys(summary.eventCounts).length}
                    </div>
                  </div>
                </div>

                {Object.keys(summary.eventCounts).length > 0 && (
                  <div>
                    <h5 className="font-medium text-gray-900 mb-2">Event Breakdown</h5>
                    <div className="space-y-2">
                      {Object.entries(summary.eventCounts).map(([event, count]) => (
                        <div key={event} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            {event.replace(/_/g, ' ')}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Security Settings</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Event Logging</span>
                    <div className="flex items-center">
                      <Eye className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-green-600">Enabled</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">XSS Detection</span>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-green-600">Active</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Rate Limiting</span>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-green-600">Active</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Input Sanitization</span>
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-green-600">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityDashboard;
